'use client'

import { shippableCountries } from '@/utils/shippableCountries'
import { Button as HeadlessButton } from '@headlessui/react'
import clsx from 'clsx'
import { IconNames } from 'lib/settings/icons'
import usaStates from 'lib/settings/usa-states'
import { AutoCompleteItem } from 'lib/types/AutoCompleteInput'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Control } from 'react-hook-form'
import Header from '../../Header/Header'
import Icon from '../../Icon/Icon'
import AutoCompleteInput from '../AutoCompleteInput/AutoCompleteInput'
import CheckboxInput from '../CheckboxInput/CheckboxInput'
import ComboboxInput from '../ComboboxInput/ComboboxInput'
import InputLabel from '../InputLabel/InputLabel'
import SelectInput from '../SelectInput/SelectInput'
import TextInput from '../TextInput/TextInput'
import ContactInput from './ContactInput'
import RecognizedPlaceCard from './RecognizedPlaceCard'

interface Port {
  id: number
  name: string
}
interface CruiseOption {
  id: number
  line: string
  ports: Port[]
}

export interface AddressInputAddress {
  address_1?: string
  address_2?: string
  attention_name?: string
  city?: string
  company_name?: string
  country?: string
  country_code?: string
  cruise_line_id?: number
  formatted_address?: string
  id?: string
  is_cruise?: boolean
  phone_number?: string
  port_id?: number
  state?: string
  zip?: string
  address_type?: string
}

// TODO: maybe remove export
export interface RecognizedPlace {
  id?: string
  name: string
  showAddress2?: boolean
  label?: string
  icon?: IconNames
  iconClassName?: string
}

interface Coded {
  code: string
  name: string
}

export interface AddressInputProps {
  /**
   * Optional default address values
   */
  address?: AddressInputAddress
  /**
   * Optional list of loading states for the autocomplete items
   */
  areAutoCompleteItemsLoading?: boolean
  /**
   * Optional contact name
   */
  attentionName?: string
  /**
   * Optional list of autocomplete items
   */
  autoCompleteItems?: AutoCompleteItem[]
  /**
   * Optional brand label
   */
  brandLabel?: string
  /**
   * Optional class name
   */
  className?: string
  /**
   * Whether the address fields should be collapsed by default
   */
  conditionalEdit?: boolean
  /**
   * The form control
   */
  control: Control
  /**
   * The list of countries
   */
  countries?: Coded[]
  /**
   * Optional list of cruise options
   */
  cruiseOptions?: CruiseOption[]
  /**
   * The encapsulating name for this field
   */
  name: string
  /**
   * Conditional change handler for the contact fields
   */
  onContactChange?: (attentionName: string, phoneNumber: string) => void
  /**
   * Conditional change handler for the country field
   */
  onCountryChange?: (countryCode: string) => void
  /**
   * Conditional change handler for the city field
   */
  onCityChange?: (city?: string) => void
  /**
   * Optional phone number
   */
  phoneNumber?: string
  /**
   * Optional recognized place functionality
   */
  recognizedPlace?: RecognizedPlace
  /**
   * Optional list of search term handlers
   */
  searchTermHandlers?: (term: string) => void
  /**
   * Optional list of select autocomplete item handlers
   */
  selectAutoCompleteItemHandlers?: (item: AutoCompleteItem) => void
  /**
   * Whether to show the contact input fields
   */
  showContact?: boolean
  /**
   * The list of states
   */
  states?: Coded[]
  /**
   * The title
   */
  title?: string
  /**
   * Address type
   */
  type: 'origin' | 'destination'
  /**
   * Optional validation function for zip code
   */
  validateZip?: () => boolean | string
  /**
   * Whether the zip code field is disabled
   */
  zipDisabled?: boolean
}

export default function AddressInput({
  address,
  attentionName,
  areAutoCompleteItemsLoading,
  autoCompleteItems = [],
  brandLabel,
  className,
  conditionalEdit = false,
  control,
  countries = [],
  cruiseOptions,
  name,
  onCityChange,
  onContactChange,
  onCountryChange,
  phoneNumber,
  recognizedPlace,
  searchTermHandlers,
  selectAutoCompleteItemHandlers,
  showContact = true,
  states = [],
  title,
  type = 'origin',
  validateZip = () => true,
  zipDisabled = false,
}: AddressInputProps) {
  const [isAddressActive, setIsAddressActive] = useState(false)
  const [isCompanyActive, setIsCompanyActive] = useState(false)
  const aptText = 'Apt #, suite, floor, etc.'

  const defaultCountry = useMemo(
    () => address?.country_code?.toLocaleUpperCase() || 'US',
    [address],
  )

  const [countryCode, setCountryCode] = useState(defaultCountry)
  const prevDefaultCountryRef = useRef(defaultCountry)

  useEffect(() => {
    if (defaultCountry !== prevDefaultCountryRef.current) {
      prevDefaultCountryRef.current = defaultCountry
      setCountryCode(defaultCountry)
    }
  }, [defaultCountry])

  const [expanded, setExpanded] = useState(
    !(conditionalEdit || recognizedPlace),
  )

  const [showCruiseOption, setShowCruiseOption] = useState(false)

  const [selectedCruiseLine, setSelectedCruiseLine] = useState(
    cruiseOptions && cruiseOptions.length > 0 && cruiseOptions[0].id,
  )

  const countryCodeOptions = useMemo(() => {
    const mapCountries = (list: { code: string; name: string }[]) =>
      list
        .map(({ code: value, name: label }) => ({ value, label }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }),
        )

    return countries.length
      ? mapCountries(countries)
      : mapCountries(shippableCountries)
  }, [countries])

  const statesList = useMemo(
    () =>
      Array.isArray(states) && states.length
        ? states
        : countryCode === 'US' && Array.isArray(usaStates)
          ? usaStates
          : [],
    [states, countryCode],
  )

  const stateOptions = useMemo(
    () =>
      statesList
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ code: value, name: label }) => ({ value, label })),
    [statesList],
  )

  const defaultState = useMemo(() => {
    const state = (address?.state ?? '').toUpperCase()

    return (
      stateOptions.find(
        (option) =>
          option.value === state || option.label?.toUpperCase() === state,
      )?.value || ''
    )
  }, [address, stateOptions])

  const formatOption = useCallback((item: AutoCompleteItem) => {
    if (!item?.address?.city) return item?.formatted_address || item.name
    // GSE-11976: include address line 1 (street) so the club row shows the full
    // street address, not just city/state/zip. The mapped autocomplete address
    // holds the street under `address1` (camelToSnakeCase leaves it unchanged),
    // so read it robustly — mirrors src/utils/autoComplete.ts.
    const addr = item.address as { address1?: string; address_1?: string }
    const street = addr.address1 ?? addr.address_1 ?? ''
    return [
      `${item.name} <br>`,
      street ? `${street},` : '',
      `${item.address?.city},`,
      item.address?.state,
      item.address?.zip,
    ]
      .filter(Boolean)
      .join(' ')
  }, [])

  const blurAddressHandler = useCallback(
    () => setIsAddressActive(false),
    [setIsAddressActive],
  )

  const blurCompanyHandler = useCallback(
    () => setIsCompanyActive(false),
    [setIsCompanyActive],
  )

  const focusCompanyHandler = useCallback(
    () => setIsCompanyActive(true),
    [setIsCompanyActive],
  )

  const focusAddressHandler = useCallback(
    () => setIsAddressActive(true),
    [setIsAddressActive],
  )

  useEffect(() => setExpanded(!recognizedPlace), [recognizedPlace])

  return (
    <div className={clsx(className)}>
      {title && (
        <Header as="h2" className="type-body-5">
          {title}
        </Header>
      )}
      {showContact && (
        <ContactInput
          contactType={type}
          control={control}
          defaults={[attentionName || '', phoneNumber || '']}
          name={name}
          onSave={onContactChange}
          note="This is the contact that will be on the shipping label."
        />
      )}
      <div className="flex flex-col mt-4 gap-y-4 md:flex-row md:flex-wrap md:gap-x-4">
        {!expanded && (
          <>
            {recognizedPlace && (
              <RecognizedPlaceCard
                address={address}
                brandLabel={brandLabel}
                recognizedPlace={recognizedPlace}
              />
            )}

            {recognizedPlace ? null : (
              <div className="w-full">
                <InputLabel>Address*</InputLabel>
                <div className="w-full flex gap-2 items-center rounded-8 text-neutral-600 bg-neutral-200 border border-neutral-500 h-12 mt-2 px-4 lg:h-14">
                  {conditionalEdit && <Icon icon="golf" className="w-4 h-4" />}
                  <span className="flex-1 overflow-hidden overflow-ellipsis whitespace-nowrap type-body-8">
                    {[
                      address?.address_1 || '',
                      address?.city || '',
                      (address?.state || '').toUpperCase(),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    {address?.zip ? ` ${address.zip}` : ''},{' '}
                    {address?.country_code?.toUpperCase()}
                  </span>
                  {conditionalEdit && (
                    <HeadlessButton onClick={() => setExpanded(true)}>
                      <Icon icon="edit" className="w-4 h-4 shrink-0" />
                    </HeadlessButton>
                  )}
                </div>
                {conditionalEdit && (
                  <button
                    className="flex items-center h-12 mt-2 type-body-7"
                    type="button"
                    onClick={() => setExpanded(true)}
                  >
                    <Icon icon="plus-circle-outline" className="w-5 h-5 mr-2" />{' '}
                    {`Add ${aptText}`}
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {expanded && cruiseOptions && cruiseOptions.length > 0 && (
          <CheckboxInput
            control={control}
            name={name + '.is_cruise'}
            label={
              type === 'origin' ? 'Pickup from a cruise' : 'Deliver to a cruise'
            }
            checked={showCruiseOption}
            onChange={setShowCruiseOption}
            className="mt-4"
          />
        )}
        {!showCruiseOption && (
          <>
            {expanded && (
              <>
                <ComboboxInput
                  control={control}
                  name={name + '.country_code'}
                  label="Country*"
                  placeholder="Choose country"
                  rules={{
                    required: { value: true, message: 'Country is required' },
                  }}
                  options={countryCodeOptions}
                  onChange={(item) => (
                    onCountryChange?.(item as string),
                    setCountryCode(item as string)
                  )}
                  defaultValue={countryCode}
                  className="w-full"
                />
                <AutoCompleteInput
                  allowManualEntry
                  showSearchIcon
                  busy={isAddressActive && areAutoCompleteItemsLoading}
                  className="w-full"
                  control={control}
                  defaultValue={address?.address_1}
                  displayValue={(item: {
                    formatted_address?: string
                    name: string
                  }) =>
                    item?.formatted_address?.split(/<br>|,/)?.[0] || item?.name
                  }
                  label="Address*"
                  autoComplete="new-password"
                  name={name + '.address_1'}
                  placeholder="Enter address"
                  options={autoCompleteItems}
                  maxLength={35}
                  rules={{
                    required: {
                      value: true,
                      message: 'Address is required',
                    },
                    maxLength: {
                      value: 35,
                      message: 'Address must be 35 characters or less',
                    },
                  }}
                  onBlur={blurAddressHandler}
                  onChange={selectAutoCompleteItemHandlers}
                  onFocus={focusAddressHandler}
                  onInput={searchTermHandlers}
                  optionFormat={formatOption}
                />
              </>
            )}
            {(recognizedPlace?.showAddress2 || expanded) && (
              <div className="flex flex-col gap-y-4 w-full md:flex-row md:gap-x-4">
                {expanded ? (
                  <AutoCompleteInput
                    allowManualEntry
                    showSearchIcon
                    busy={isCompanyActive && areAutoCompleteItemsLoading}
                    className="flex-1"
                    control={control}
                    defaultValue={address?.company_name}
                    displayValue={(item: {
                      formatted_address?: string
                      name: string
                    }) =>
                      item?.formatted_address?.split(/<br>|,/)?.[0] ||
                      item?.name
                    }
                    label="Company name (optional)"
                    name={name + '.company_name'}
                    options={autoCompleteItems}
                    placeholder="Enter company"
                    autoComplete="new-password"
                    maxLength={35}
                    rules={{
                      maxLength: {
                        value: 35,
                        message: 'Company name must be 35 characters or less',
                      },
                    }}
                    onBlur={blurCompanyHandler}
                    onChange={selectAutoCompleteItemHandlers}
                    onFocus={focusCompanyHandler}
                    onInput={searchTermHandlers}
                    optionFormat={formatOption}
                  />
                ) : null}
                <TextInput
                  control={control}
                  name={name + '.address_2'}
                  htmlName="prevent-autofill"
                  autoComplete="off"
                  label={`${aptText} (optional)`}
                  placeholder="Enter Apt #, suite, floor, etc."
                  defaultValue={address?.address_2}
                  className="flex-1"
                  maxLength={35}
                  rules={{
                    maxLength: {
                      value: 35,
                      message: 'Address line 2 must be 35 characters or less',
                    },
                  }}
                />
              </div>
            )}
            {expanded && (
              <div className="flex flex-col gap-y-4 w-full md:flex-row md:gap-x-4">
                <TextInput
                  control={control}
                  name={name + '.city'}
                  htmlName="prevent-autofill"
                  autoComplete="none"
                  label="City*"
                  placeholder="Enter city"
                  maxLength={35}
                  rules={{
                    required: { value: true, message: 'City is required' },
                    maxLength: {
                      value: 35,
                      message: 'City must be 35 characters or less',
                    },
                  }}
                  defaultValue={address?.city}
                  className="flex-1"
                  onChange={(data) =>
                    onCityChange && onCityChange(data as string | undefined)
                  }
                />
                {stateOptions.length === 0 ? (
                  <TextInput
                    control={control}
                    name={name + '.state'}
                    htmlName="prevent-autofill"
                    autoComplete="none"
                    label={countryCode === 'US' ? 'State*' : 'State/Province*'}
                    placeholder={
                      countryCode === 'US'
                        ? 'Enter state'
                        : 'Enter state/province'
                    }
                    rules={{
                      required: { value: true, message: 'State is required' },
                    }}
                    defaultValue={address?.state}
                    className="flex-1"
                    onChange={(data) => {
                      console.log('data', data)
                    }}
                  />
                ) : (
                  <ComboboxInput
                    key={`${defaultState}-${stateOptions.length}-${countryCode}`}
                    control={control}
                    name={name + '.state'}
                    label={countryCode === 'US' ? 'State*' : 'State/Province*'}
                    placeholder={
                      countryCode === 'US'
                        ? 'Choose state'
                        : 'Choose state/province'
                    }
                    rules={{
                      required: { value: true, message: 'State is required' },
                    }}
                    options={stateOptions}
                    defaultValue={defaultState}
                    className="flex-1"
                  />
                )}
                {!zipDisabled && (
                  <TextInput
                    control={control}
                    name={name + '.zip'}
                    htmlName="prevent-autofill"
                    autoComplete="none"
                    label={countryCode === 'US' ? 'Zip code*' : 'Postal code*'}
                    placeholder={
                      countryCode === 'US'
                        ? 'Enter zip code'
                        : 'Enter postal code'
                    }
                    maxLength={10}
                    rules={
                      countryCode === 'US'
                        ? {
                            required: {
                              value: true,
                              message: 'Zip code is required',
                            },
                            pattern: {
                              value: /[0-9]{4,}/,
                              message: 'Please provide a valid Zip Code',
                            },
                            maxLength: {
                              value: 10,
                              message: 'Zip code must be 10 characters or less',
                            },
                            validate: { validateZip },
                          }
                        : {
                            required: {
                              value: true,
                              message: 'Postal code is required',
                            },
                            pattern: {
                              value:
                                /^[A-Za-z0-9][A-Za-z0-9\-\s]{2,10}[A-Za-z0-9]$/,
                              message: 'Please provide a valid Postal Code',
                            },
                            maxLength: {
                              value: 10,
                              message:
                                'Postal code must be 10 characters or less',
                            },
                            validate: { validateZip },
                          }
                    }
                    defaultValue={address?.zip}
                    className="flex-1"
                  />
                )}
              </div>
            )}
          </>
        )}
        {expanded &&
          showCruiseOption &&
          cruiseOptions &&
          cruiseOptions.length > 0 && (
            <>
              <SelectInput
                control={control}
                name={name + '.cruise_line_id'}
                label="Cruise Line*"
                placeholder="Choose cruise line"
                rules={{
                  required: {
                    value: true,
                    message: 'Cruise line is required',
                  },
                }}
                options={cruiseOptions.map(({ id, line }) => ({
                  value: id,
                  label: line,
                }))}
                onChange={(value) => setSelectedCruiseLine(value as number)}
                defaultValue={cruiseOptions[0].id}
                className="w-full"
              />
              <SelectInput
                control={control}
                name={name + '.port_id'}
                label="Port*"
                placeholder="Choose port"
                rules={{
                  required: { value: true, message: 'Port is required' },
                }}
                options={cruiseOptions[
                  cruiseOptions.findIndex(({ id }) => id === selectedCruiseLine)
                ].ports.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
                defaultValue={
                  cruiseOptions[
                    cruiseOptions.findIndex(
                      ({ id }) => id === selectedCruiseLine,
                    )
                  ].ports[0].id
                }
                className="w-full"
              />
            </>
          )}
      </div>
    </div>
  )
}
